import { arrayMove } from "@dnd-kit/sortable";
import type { CohortConditionSummary, CreatorCohortBatchPayload, CreatorTagColor } from "@unbox/api";
import { useEffect, useRef, useState } from "react";
import { useBatchSaveCreatorCohorts, useCreatorCohorts, useDownloadCreatorCohortTemplate } from "@/api/creator-cohorts";
import { randomTagColor } from "@/lib/creator-tags/colors";
import { error, success } from "@/providers/ToastProvider";

// A staged row in the editor: either an existing server cohort (with a server
// `id`) or a brand-new one (client-generated UUID). All edits live here until
// Save commits them as a single batch.
export type DraftRow = {
  id: string;
  name: string;
  description: string;
  color: CreatorTagColor;
  source: "manual" | "csv";
  userId?: string;
  /** Display name of the cohort's owner (creating user); empty for unsaved rows. */
  ownerName: string;
  assignedCount: number;
  ruleConditions: CohortConditionSummary[];
};

const makeNewCohortRow = (): DraftRow => ({
  id: crypto.randomUUID(),
  name: "",
  description: "",
  color: randomTagColor(),
  source: "manual",
  ownerName: "",
  assignedCount: 0,
  ruleConditions: [],
});

// Scroll the just-added (empty) row into view and focus its name input. Runs on the
// next frame so the row has rendered, and after the dialog's own open-focus has settled.
const focusNewCohortRow = (container: HTMLDivElement | null) => {
  if (!container) return;
  requestAnimationFrame(() => {
    container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    const inputs = container.querySelectorAll<HTMLInputElement>('input[aria-label="Cohort name"]');
    inputs[inputs.length - 1]?.focus();
  });
};

/**
 * Shared editing state for the cohort library, used by the Manage All Cohorts
 * modal wherever it is mounted. Owns the draft, the staged removals, the diff
 * against the server snapshot, and save; the container supplies the chrome
 * (footer / discard guard).
 *
 * `active` gates seeding: the modal passes its `open` flag, so reopening starts
 * from the list as it now stands rather than the last abandoned edit.
 */
export const useCohortLibraryEditor = ({
  active,
  startWithNewCohort = false,
}: {
  active: boolean;
  startWithNewCohort?: boolean;
}) => {
  const { data: serverCohorts } = useCreatorCohorts();
  const { mutateAsync: saveBatch, isPending } = useBatchSaveCreatorCohorts();
  const downloadTemplate = useDownloadCreatorCohortTemplate();

  const [draft, setDraft] = useState<DraftRow[]>([]);
  const [search, setSearch] = useState("");
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());

  // Snapshot of the server state at seed time, used to compute the diff on Save.
  const original = useRef<Map<string, { name: string; description: string; color: string }>>(new Map());
  const originalOrder = useRef<string[]>([]);
  const seeded = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Seed the editor from the server list once per activation; reset on deactivation.
  useEffect(() => {
    if (active && serverCohorts && !seeded.current) {
      original.current = new Map(
        serverCohorts.map((c) => [c.id, { name: c.name, description: c.description ?? "", color: c.color }]),
      );
      originalOrder.current = serverCohorts.map((c) => c.id);
      const seededRows: DraftRow[] = serverCohorts.map((c) => ({
        id: c.id,
        name: c.name,
        description: c.description ?? "",
        color: c.color as CreatorTagColor,
        source: c.source,
        userId: c.userId,
        ownerName: [c.ownerFirstName, c.ownerLastName].filter(Boolean).join(" "),
        assignedCount: c.assignedCount,
        ruleConditions: c.ruleConditions,
      }));
      setDraft(startWithNewCohort ? [...seededRows, makeNewCohortRow()] : seededRows);
      if (startWithNewCohort) focusNewCohortRow(scrollContainerRef.current);
      seeded.current = true;
    }
    if (!active) {
      seeded.current = false;
      setDraft([]);
      setSearch("");
      setRemovedIds(new Set());
    }
  }, [active, serverCohorts, startWithNewCohort]);

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate();
    } catch {
      error("Could not download the template");
    }
  };

  const updateRow = (id: string, patch: Partial<DraftRow>) =>
    setDraft((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));

  const addNewCohort = () => {
    setDraft((rows) => [...rows, makeNewCohortRow()]);
    // A filtered list would hide the row that was just added.
    setSearch("");
    focusNewCohortRow(scrollContainerRef.current);
  };

  const toggleRemoved = (id: string) =>
    setRemovedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const isRemoved = (id: string) => removedIds.has(id);

  const moveRow = (activeId: string, overId: string) =>
    setDraft((rows) => {
      const from = rows.findIndex((row) => row.id === activeId);
      const to = rows.findIndex((row) => row.id === overId);
      if (from === -1 || to === -1 || from === to) return rows;
      return arrayMove(rows, from, to);
    });

  // Forces the draft to re-seed from the server once the (already-invalidated)
  // cohort list refetches — for saves that bypass this hook's own handleSave/
  // saveBatch path, e.g. the CSV import.
  const markStale = () => {
    seeded.current = false;
  };

  // Diff the current draft against the seed snapshot to build the save payload.
  const computeBatch = (): CreatorCohortBatchPayload => {
    const create: CreatorCohortBatchPayload["create"] = [];
    const update: CreatorCohortBatchPayload["update"] = [];
    const remove: string[] = [];
    const surviving = draft.filter((row) => !removedIds.has(row.id)).map((row) => row.id);
    const survivingIds = new Set(surviving);

    for (const row of draft) {
      if (removedIds.has(row.id)) continue;
      const name = row.name.trim();
      const before = original.current.get(row.id);
      if (!before) {
        create.push({ name, color: row.color, ref: row.id });
        continue;
      }
      if (before.name !== name || before.color !== row.color) {
        update.push({ id: row.id, name, color: row.color });
      }
    }
    for (const id of original.current.keys()) {
      if (!survivingIds.has(id)) remove.push(id);
    }

    return { create, update, delete: remove, order: surviving };
  };

  const isBatchDirty = (batch: CreatorCohortBatchPayload) =>
    batch.create.length > 0 || batch.update.length > 0 || batch.delete.length > 0 || isReordered(batch);

  // A delete alone shifts every id after it, so the saved order is only a change
  // of its own once the cohorts this save keeps are compared in isolation.
  const isReordered = (batch: CreatorCohortBatchPayload) => {
    const kept = originalOrder.current.filter((id) => !batch.delete.includes(id));
    const staying = (batch.order ?? []).filter((id) => original.current.has(id));
    return kept.some((id, index) => staying[index] !== id);
  };

  const isDirty = isBatchDirty(computeBatch());

  /**
   * Validate + persist the batch. Returns whether the save (or no-op) succeeded
   * so the container can decide what to do next. On success the snapshot is
   * marked stale so it re-seeds from the refreshed server list.
   */
  const handleSave = async (): Promise<boolean> => {
    if (draft.some((row) => !removedIds.has(row.id) && row.name.trim() === "")) {
      error("Every cohort needs a name");
      return false;
    }
    const batch = computeBatch();
    if (!isBatchDirty(batch)) return true;
    try {
      await saveBatch(batch);
      success("Cohort library saved");
      setRemovedIds(new Set());
      seeded.current = false;
      return true;
    } catch {
      // Global handler surfaces the error toast; keep edits so the user can fix
      // the offending cohort (e.g. a duplicate name).
      return false;
    }
  };

  const visibleRows = draft.filter(
    (row) => !row.id || search.trim() === "" || row.name.toLowerCase().includes(search.trim().toLowerCase()),
  );

  return {
    draft,
    visibleRows,
    search,
    setSearch,
    isRemoved,
    removedCount: removedIds.size,
    toggleRemoved,
    scrollContainerRef,
    isPending,
    isDirty,
    updateRow,
    moveRow,
    addNewCohort,
    markStale,
    handleSave,
    handleDownloadTemplate,
  };
};

export type CohortLibraryEditor = ReturnType<typeof useCohortLibraryEditor>;
